import {
  isLegislationTypeCode,
  type LegislationTypeCode,
} from '@artem-lex/common-server/radar/legislationTypes'
import type {
  ScopeTierInForceFilter,
  ScopeTierStatusFilter,
} from '@/shared/hooks/api/radarScopeTier'

export type ScopeTierUrlFilters = {
  query: string
  tiers: Array<1 | 2 | 3>
  types: LegislationTypeCode[]
  includeUnassigned: boolean
  inForceFilter: ScopeTierInForceFilter
  status: ScopeTierStatusFilter
  includeTrashed: boolean
}

const DEFAULT_FILTERS: ScopeTierUrlFilters = {
  query: '',
  tiers: [1, 2, 3],
  types: [],
  includeUnassigned: false,
  inForceFilter: 'in_force',
  status: 'all',
  includeTrashed: false,
}

export function parseScopeTierUrlFilters(params: URLSearchParams): ScopeTierUrlFilters {
  const query = params.get('q') ?? DEFAULT_FILTERS.query
  const tiersParam = params.get('tiers')
  const includeUnassigned = params.get('includeUnassigned') === 'true'
  const includeTrashed = params.get('includeTrashed') === 'true'
  const inForceFilter = parseInForceFilter(params.get('inForceFilter'))
  const status = (params.get('status') as ScopeTierStatusFilter | null) ?? DEFAULT_FILTERS.status

  const tiers = parseTiers(tiersParam)
  const types = parseTypes(params.get('types'))

  return {
    query,
    tiers,
    types,
    includeUnassigned,
    inForceFilter,
    status,
    includeTrashed,
  }
}

function parseInForceFilter(raw: string | null): ScopeTierInForceFilter {
  if (raw === 'in_force' || raw === 'not_in_force' || raw === 'all') {
    return raw
  }
  return DEFAULT_FILTERS.inForceFilter
}

export function applyScopeTierUrlFilters(
  params: URLSearchParams,
  input: ScopeTierUrlFilters
): URLSearchParams {
  const next = new URLSearchParams(params)

  writeOrDelete(next, 'q', input.query, DEFAULT_FILTERS.query)
  writeOrDelete(next, 'status', input.status, DEFAULT_FILTERS.status)
  writeOrDelete(next, 'inForceFilter', input.inForceFilter, DEFAULT_FILTERS.inForceFilter)
  writeBoolean(next, 'includeUnassigned', input.includeUnassigned)
  writeBoolean(next, 'includeTrashed', input.includeTrashed)

  const tiersValue =
    input.tiers.length === 0 ? 'none' : input.tiers.length === DEFAULT_FILTERS.tiers.length ? '' : input.tiers.join(',')
  writeOrDelete(next, 'tiers', tiersValue, '')

  const typesValue = input.types.length > 0 ? input.types.join(',') : ''
  writeOrDelete(next, 'types', typesValue, '')

  return next
}

export function getInvalidTypeTokensFromParam(raw: string | null): string[] {
  if (!raw) {
    return []
  }
  return raw
    .split(',')
    .map((value) => value.trim().toUpperCase())
    .filter((value) => value.length > 0 && !isLegislationTypeCode(value))
}

export function shouldRewriteTypesParam(
  raw: string | null,
  parsed: LegislationTypeCode[]
): boolean {
  if (getInvalidTypeTokensFromParam(raw).length > 0) {
    return true
  }
  if (!raw) {
    return false
  }
  const normalizedParam = raw
    .split(',')
    .map((value) => value.trim().toUpperCase())
    .filter((value) => value.length > 0 && isLegislationTypeCode(value))
  const normalizedParsed = parsed.map((value) => value.toUpperCase())
  return normalizedParam.join(',') !== normalizedParsed.join(',')
}

function parseTypes(typesParam: string | null): LegislationTypeCode[] {
  if (!typesParam) {
    return []
  }
  const seen = new Set<LegislationTypeCode>()
  const parsed: LegislationTypeCode[] = []
  for (const segment of typesParam.split(',')) {
    const token = segment.trim().toUpperCase()
    if (token.length === 0 || !isLegislationTypeCode(token) || seen.has(token)) {
      continue
    }
    seen.add(token)
    parsed.push(token)
  }
  return parsed
}

function parseTiers(tiersParam: string | null): Array<1 | 2 | 3> {
  if (!tiersParam) {
    return [...DEFAULT_FILTERS.tiers]
  }
  const trimmed = tiersParam.trim().toLowerCase()
  if (trimmed === 'none') {
    return []
  }
  const tiers = tiersParam
    .split(',')
    .map((value) => Number.parseInt(value.trim(), 10))
    .filter((value): value is 1 | 2 | 3 => value === 1 || value === 2 || value === 3)
  if (tiers.length === 0) {
    return [...DEFAULT_FILTERS.tiers]
  }
  return Array.from(new Set(tiers)).sort()
}

function writeBoolean(params: URLSearchParams, key: string, value: boolean) {
  if (value) {
    params.set(key, 'true')
  } else {
    params.delete(key)
  }
}

function writeOrDelete(params: URLSearchParams, key: string, value: string, defaultValue: string) {
  if (!value || value === defaultValue) {
    params.delete(key)
  } else {
    params.set(key, value)
  }
}
